import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Eye, EyeOff, Lock, Unlock, ChevronDown, ChevronUp, Users, CheckCircle, XCircle, Award, Gift, Clock, Edit2, Calendar, Trophy, Upload, Image, MapPin, Camera } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatTimeLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return "Expired";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m left`;
  if (m > 0) return `${m}m ${s}s left`;
  return `${s}s left`;
};

// Convert ISO UTC string to local datetime-local format (YYYY-MM-DDTHH:mm)
const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* ── Seasons Manager Sub-Tab ── */
const SeasonsManager = () => {
  const [seasons, setSeasons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [editId, setEditId] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [viewingSeasonId, setViewingSeasonId] = useState(null);

  const fetchSeasons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trivia/admin/seasons`, { headers: getAuthHeader() });
      setSeasons(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/trivia/admin/seasons/${editId}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/trivia/admin/seasons`, form, { headers: getAuthHeader() });
      }
      setShowForm(false); setEditId(null); setForm({ name: "", start_date: "", end_date: "" });
      fetchSeasons();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const deleteSeason = async (id) => {
    if (!window.confirm("Delete this season?")) return;
    await axios.delete(`${API}/trivia/admin/seasons/${id}`, { headers: getAuthHeader() });
    fetchSeasons();
  };

  const viewLeaderboard = async (seasonId) => {
    if (viewingSeasonId === seasonId) { setViewingSeasonId(null); setLeaderboard(null); return; }
    try {
      const res = await axios.get(`${API}/trivia/admin/leaderboard?season_id=${seasonId}`, { headers: getAuthHeader() });
      setLeaderboard(res.data);
      setViewingSeasonId(seasonId);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">Trivia Seasons</h3>
        <button onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ name: "", start_date: "", end_date: "" }); }}
          className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs uppercase" data-testid="season-create-btn">
          <Plus className="w-3.5 h-3.5" /> New Season
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-800 border border-white/10 p-4 space-y-3">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Season Name (e.g. Fall 2026)" className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="season-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase block mb-1">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="season-start-input" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase block mb-1">End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} required className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="season-end-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-orange-500 text-black font-bold text-xs uppercase" data-testid="season-save-btn">{editId ? "Update" : "Create"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-zinc-700 text-white text-xs uppercase">Cancel</button>
          </div>
        </form>
      )}

      {seasons.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-6">No seasons yet. Create one to track leaderboards!</p>
      ) : (
        <div className="space-y-2">
          {seasons.map(s => (
            <div key={s.id} className={`border ${s.is_active ? "border-green-500/30 bg-green-500/5" : "border-white/5 bg-zinc-900/50"}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{s.name}</span>
                    {s.is_active && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-green-500/20 text-green-400">ACTIVE</span>}
                  </div>
                  <span className="text-zinc-500 text-xs">{s.start_date} — {s.end_date}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => viewLeaderboard(s.id)} className="p-2 text-zinc-500 hover:text-yellow-500" title="View Leaderboard" data-testid={`season-leaderboard-${s.id}`}>
                    <Trophy className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setForm({ name: s.name, start_date: s.start_date, end_date: s.end_date }); setEditId(s.id); setShowForm(true); }}
                    className="p-2 text-zinc-500 hover:text-white"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => deleteSeason(s.id)} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {viewingSeasonId === s.id && leaderboard && (
                <div className="border-t border-white/5 px-4 py-4">
                  <h4 className="text-zinc-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Trophy className="w-3.5 h-3.5 text-yellow-500" /> Leaderboard — {leaderboard.season?.name}
                  </h4>
                  {leaderboard.leaderboard.length === 0 ? (
                    <p className="text-zinc-600 text-sm text-center py-4">No results yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left text-zinc-500 text-[10px] uppercase py-2 px-2">#</th>
                            <th className="text-left text-zinc-500 text-[10px] uppercase py-2 px-2">Player</th>
                            <th className="text-left text-zinc-500 text-[10px] uppercase py-2 px-2">Hometown</th>
                            <th className="text-left text-zinc-500 text-[10px] uppercase py-2 px-2">Photo</th>
                            <th className="text-right text-zinc-500 text-[10px] uppercase py-2 px-2">Correct</th>
                            <th className="text-right text-zinc-500 text-[10px] uppercase py-2 px-2">Total</th>
                            <th className="text-left text-zinc-500 text-[10px] uppercase py-2 px-2">Social OK</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard.leaderboard.map(e => (
                            <tr key={e.user_id} className="border-b border-white/5">
                              <td className={`py-2 px-2 font-bold ${e.rank <= 3 ? "text-yellow-500" : "text-zinc-500"}`}>{e.rank}</td>
                              <td className="py-2 px-2 text-white">{e.name || e.username}</td>
                              <td className="py-2 px-2 text-zinc-400">{e.hometown || "—"}</td>
                              <td className="py-2 px-2">
                                {e.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${e.photo_url}`} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="text-zinc-600 text-xs">—</span>}
                              </td>
                              <td className="py-2 px-2 text-right text-green-400 font-bold">{e.correct}</td>
                              <td className="py-2 px-2 text-right text-zinc-400">{e.total}</td>
                              <td className="py-2 px-2">{e.social_media_consent ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-zinc-600" />}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ── Main TriviaTab ── */
export const TriviaTab = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [, setTick] = useState(0);
  const [activeTab, setActiveTab] = useState("questions"); // questions | seasons
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    title: "", presented_by_name: "", presented_by_logo_url: "", question: "",
    type: "multiple_choice", options: ["", "", "", ""],
    correct_answer: "", prize: "", is_active: false, timer_minutes: "",
    scheduled_for: ""
  });

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trivia/admin/questions`, { headers: getAuthHeader() });
      setQuestions(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  useEffect(() => {
    const hasTimers = questions.some(q => q.expires_at && q.is_active && !q.is_locked);
    if (!hasTimers) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [questions]);

  const fetchDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/trivia/admin/questions/${id}`, { headers: getAuthHeader() });
      setDetailData(res.data);
    } catch (e) { console.error(e); }
  };

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); setDetailData(null); }
    else { setExpandedId(id); fetchDetail(id); }
  };

  const resetForm = () => {
    setForm({ title: "", presented_by_name: "", presented_by_logo_url: "", question: "", type: "multiple_choice", options: ["", "", "", ""], correct_answer: "", prize: "", is_active: false, timer_minutes: "", scheduled_for: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (q) => {
    setForm({
      title: q.title || "", presented_by_name: q.presented_by_name || "", presented_by_logo_url: q.presented_by_logo_url || "",
      question: q.question, type: q.type, options: q.options.length ? q.options : ["", "", "", ""],
      correct_answer: q.correct_answer || "", prize: q.prize || "", is_active: q.is_active,
      timer_minutes: q.timer_minutes || "", scheduled_for: isoToLocal(q.scheduled_for)
    });
    setEditingId(q.id);
    setShowForm(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/trivia/admin/upload-logo`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      setForm({ ...form, presented_by_logo_url: res.data.url });
    } catch (e) { alert("Logo upload failed"); }
    setUploadingLogo(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      options: form.type === "multiple_choice" ? form.options.filter(o => o.trim()) : [],
      timer_minutes: form.timer_minutes ? parseInt(form.timer_minutes, 10) : null,
      scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null
    };
    try {
      if (editingId) {
        await axios.put(`${API}/trivia/admin/questions/${editingId}`, payload, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/trivia/admin/questions`, payload, { headers: getAuthHeader() });
      }
      resetForm();
      fetchQuestions();
    } catch (e) { alert(e.response?.data?.detail || "Error saving question"); }
  };

  const toggleActive = async (id) => {
    await axios.put(`${API}/trivia/admin/questions/${id}/toggle-active`, {}, { headers: getAuthHeader() });
    fetchQuestions();
  };

  const toggleLock = async (id) => {
    await axios.put(`${API}/trivia/admin/questions/${id}/toggle-lock`, {}, { headers: getAuthHeader() });
    fetchQuestions();
    if (expandedId === id) fetchDetail(id);
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Delete this trivia question and all responses?")) return;
    await axios.delete(`${API}/trivia/admin/questions/${id}`, { headers: getAuthHeader() });
    if (expandedId === id) { setExpandedId(null); setDetailData(null); }
    fetchQuestions();
  };

  const updateOption = (idx, val) => { const opts = [...form.options]; opts[idx] = val; setForm({ ...form, options: opts }); };
  const addOption = () => setForm({ ...form, options: [...form.options, ""] });
  const removeOption = (idx) => setForm({ ...form, options: form.options.filter((_, i) => i !== idx) });

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="trivia-admin-tab">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1">
          <button onClick={() => setActiveTab("questions")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === "questions" ? "bg-white text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`} data-testid="trivia-tab-questions">
            Questions ({questions.length})
          </button>
          <button onClick={() => setActiveTab("seasons")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === "seasons" ? "bg-white text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`} data-testid="trivia-tab-seasons">
            <Trophy className="w-3.5 h-3.5 inline mr-1" />Seasons & Leaderboard
          </button>
        </div>
        {activeTab === "questions" && (
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm uppercase transition-colors" data-testid="trivia-create-btn">
            <Plus className="w-4 h-4" /> New Question
          </button>
        )}
      </div>

      {/* Seasons Tab */}
      {activeTab === "seasons" && <SeasonsManager />}

      {/* Questions Tab */}
      {activeTab === "questions" && (
        <>
          {/* Create/Edit Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="bg-zinc-900 border border-white/10 p-6 space-y-4" data-testid="trivia-form">
              <h3 className="text-white font-bold text-sm uppercase tracking-wider">{editingId ? "Edit Question" : "New Trivia Question"}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Title / Label</label>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monday Night Trivia" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-title-input" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Prize</label>
                  <input value={form.prize} onChange={e => setForm({ ...form, prize: e.target.value })} placeholder="e.g. $50 Gift Card" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-prize-input" />
                </div>
              </div>

              {/* Presented By Section */}
              <div className="border border-white/5 bg-zinc-800/30 p-4 space-y-3">
                <label className="text-orange-500 text-xs font-bold uppercase tracking-wider block">Presented By</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase block mb-1">Sponsor Name</label>
                    <input value={form.presented_by_name} onChange={e => setForm({ ...form, presented_by_name: e.target.value })} placeholder="e.g. Local Pizza Co." className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-presented-by-name" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase block mb-1">Logo URL (or upload below)</label>
                    <input value={form.presented_by_logo_url} onChange={e => setForm({ ...form, presented_by_logo_url: e.target.value })} placeholder="https://..." className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-presented-by-logo-url" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 bg-zinc-900 border border-white/10 hover:border-white/30 text-zinc-300 text-xs uppercase">
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingLogo ? "Uploading..." : "Upload Logo"}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                  {form.presented_by_logo_url && (
                    <img src={form.presented_by_logo_url} alt="Logo Preview" className="h-8 object-contain max-w-[120px] bg-white/5 px-2 py-1" />
                  )}
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Question *</label>
                <textarea value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} required rows={2} placeholder="Enter your trivia question..." className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm resize-none" data-testid="trivia-question-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">Answer Type</label>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setForm({ ...form, type: "multiple_choice" })} className={`px-4 py-2 text-xs font-bold uppercase border transition-colors ${form.type === "multiple_choice" ? "bg-orange-500 text-black border-orange-500" : "bg-zinc-800 text-zinc-400 border-white/10 hover:border-white/30"}`} data-testid="trivia-type-mc">Multiple Choice</button>
                    <button type="button" onClick={() => setForm({ ...form, type: "text" })} className={`px-4 py-2 text-xs font-bold uppercase border transition-colors ${form.type === "text" ? "bg-orange-500 text-black border-orange-500" : "bg-zinc-800 text-zinc-400 border-white/10 hover:border-white/30"}`} data-testid="trivia-type-text">Type Answer</button>
                  </div>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Clock className="w-3 h-3 inline mr-1" />Timer (minutes, optional)</label>
                  <input value={form.timer_minutes} onChange={e => setForm({ ...form, timer_minutes: e.target.value })} type="number" min="1" placeholder="e.g. 60" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-timer-input" />
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Calendar className="w-3 h-3 inline mr-1" />Schedule Activation (optional)</label>
                <input value={form.scheduled_for} onChange={e => setForm({ ...form, scheduled_for: e.target.value })} type="datetime-local" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-scheduled-input" />
              </div>

              {form.type === "multiple_choice" && (
                <div className="space-y-2">
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block">Answer Options</label>
                  {form.options.map((opt, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-zinc-500 text-xs w-6">{String.fromCharCode(65 + i)}.</span>
                      <input value={opt} onChange={e => updateOption(i, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + i)}`} className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid={`trivia-option-${i}`} />
                      {form.options.length > 2 && <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  {form.options.length < 8 && <button type="button" onClick={addOption} className="text-orange-500 hover:text-orange-400 text-xs font-bold uppercase">+ Add Option</button>}
                </div>
              )}

              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Correct Answer *</label>
                {form.type === "multiple_choice" ? (
                  <select value={form.correct_answer} onChange={e => setForm({ ...form, correct_answer: e.target.value })} required className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-correct-answer-select">
                    <option value="">Select correct answer...</option>
                    {form.options.filter(o => o.trim()).map((opt, i) => <option key={i} value={opt}>{String.fromCharCode(65 + i)}. {opt}</option>)}
                  </select>
                ) : (
                  <input value={form.correct_answer} onChange={e => setForm({ ...form, correct_answer: e.target.value })} required placeholder="Enter the correct answer" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="trivia-correct-answer-input" />
                )}
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-orange-500" data-testid="trivia-active-checkbox" />
                  <span className="text-zinc-300 text-sm">Set as active question</span>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm uppercase transition-colors" data-testid="trivia-save-btn">{editingId ? "Update" : "Create"} Question</button>
                <button type="button" onClick={resetForm} className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm uppercase transition-colors">Cancel</button>
              </div>
            </form>
          )}

          {/* Questions List */}
          <div className="space-y-2" data-testid="trivia-questions-list">
            {questions.length === 0 ? (
              <div className="text-center py-16 bg-zinc-900/50 border border-white/5">
                <Award className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No trivia questions yet. Create your first one!</p>
              </div>
            ) : questions.map(q => (
              <div key={q.id} className={`border ${q.is_active ? "border-green-500/30 bg-green-500/5" : "border-white/5 bg-zinc-900/50"}`} data-testid={`trivia-question-${q.id}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleExpand(q.id)}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {q.is_active && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-green-500/20 text-green-400">ACTIVE</span>}
                      {q.is_locked && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-red-500/20 text-red-400">LOCKED</span>}
                      {q.timer_minutes && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-blue-500/20 text-blue-400"><Clock className="w-2.5 h-2.5 inline mr-0.5" />{q.timer_minutes}m</span>}
                      {q.scheduled_for && !q.is_active && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-purple-500/20 text-purple-400">
                          <Calendar className="w-2.5 h-2.5 inline mr-0.5" />
                          {new Date(q.scheduled_for).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      )}
                      {q.is_active && q.expires_at && !q.is_locked && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-yellow-500/20 text-yellow-400">{formatTimeLeft(q.expires_at)}</span>
                      )}
                      {q.title && <span className="text-zinc-500 text-xs">{q.title}</span>}
                      {q.presented_by_name && <span className="text-orange-500 text-xs">Presented by {q.presented_by_name}</span>}
                    </div>
                    <p className="text-white text-sm font-medium truncate">{q.question}</p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-zinc-500 text-xs"><Users className="w-3 h-3 inline mr-1" />{q.total_responses} responses</span>
                      <span className="text-green-500 text-xs"><CheckCircle className="w-3 h-3 inline mr-1" />{q.correct_responses} correct</span>
                      {q.prize && <span className="text-yellow-500 text-xs"><Gift className="w-3 h-3 inline mr-1" />{q.prize}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleActive(q.id)} title={q.is_active ? "Deactivate" : "Activate"} className={`p-2 transition-colors ${q.is_active ? "text-green-400 hover:text-green-300" : "text-zinc-600 hover:text-white"}`} data-testid={`trivia-toggle-active-${q.id}`}>
                      {q.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => toggleLock(q.id)} title={q.is_locked ? "Unlock" : "Lock"} className={`p-2 transition-colors ${q.is_locked ? "text-red-400 hover:text-red-300" : "text-zinc-600 hover:text-white"}`} data-testid={`trivia-toggle-lock-${q.id}`}>
                      {q.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleEdit(q)} className="p-2 text-zinc-600 hover:text-white transition-colors" data-testid={`trivia-edit-${q.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteQuestion(q.id)} className="p-2 text-zinc-600 hover:text-red-400 transition-colors" data-testid={`trivia-delete-${q.id}`}><Trash2 className="w-4 h-4" /></button>
                    <button onClick={() => handleExpand(q.id)} className="p-2 text-zinc-600 hover:text-white transition-colors">
                      {expandedId === q.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedId === q.id && detailData && (
                  <div className="border-t border-white/5 px-4 py-4 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-zinc-800 p-3">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Correct Answer</p>
                        <p className="text-green-400 text-sm font-bold mt-1">{detailData.correct_answer}</p>
                      </div>
                      <div className="bg-zinc-800 p-3">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Responses</p>
                        <p className="text-white text-lg font-bold mt-1">{detailData.total_responses}</p>
                      </div>
                      <div className="bg-zinc-800 p-3">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Correct</p>
                        <p className="text-green-400 text-lg font-bold mt-1">{detailData.correct_responses}</p>
                      </div>
                      <div className="bg-zinc-800 p-3">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Incorrect</p>
                        <p className="text-red-400 text-lg font-bold mt-1">{detailData.total_responses - detailData.correct_responses}</p>
                      </div>
                    </div>

                    {detailData.responses && detailData.responses.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Result</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Name</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Email</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Phone</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Hometown</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Answer</th>
                              <th className="text-left text-zinc-500 text-[10px] uppercase tracking-wider py-2 px-2">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.responses.map((r, i) => (
                              <tr key={r.id || i} className="border-b border-white/5">
                                <td className="py-2 px-2">{r.is_correct ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}</td>
                                <td className="py-2 px-2 text-white">{r.name}</td>
                                <td className="py-2 px-2 text-zinc-400">{r.email}</td>
                                <td className="py-2 px-2 text-zinc-400">{r.phone}</td>
                                <td className="py-2 px-2 text-zinc-400">{r.city || "—"}</td>
                                <td className={`py-2 px-2 ${r.is_correct ? "text-green-400" : "text-red-400"}`}>{r.answer}</td>
                                <td className="py-2 px-2 text-zinc-600 text-xs">{new Date(r.submitted_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-zinc-600 text-sm text-center py-4">No responses yet</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
